import { useState, useMemo, useEffect } from"react";
import { ChevronRight, ChevronDown, Search, X } from"lucide-react";
import { CLASSES } from"@/data/classMeta";
import { buildTree, searchAccounts } from"@/lib/pcgUtils";
import Logo from"@/components/Logo";

const TreeNode = ({ node, depth, onSelect, selectedNum, expandedSet, toggleNode, classColor }) => {
 const hasChildren = node.children && node.children.length > 0;
 const isExpanded = expandedSet.has(node.num);
 const isSelected = selectedNum === node.num;

 return (
 <div>
 <div
 data-testid={`tree-node-${node.num}`}
 onClick={() => onSelect(node.num)}
 className={`flex items-center gap-2 py-1.5 pr-2 rounded-md cursor-pointer transition-all group ${
 isSelected
 ?"bg-[#15294F] text-white"
 :"text-slate-700 hover:bg-slate-100"
 }`}
 style={{ paddingLeft: `${8 + depth * 14}px` }}
 >
 {hasChildren ? (
 <button
 data-testid={`tree-toggle-${node.num}`}
 onClick={(e) => {
 e.stopPropagation();
 toggleNode(node.num);
 }}
 className={`shrink-0 w-4 h-4 flex items-center justify-center rounded ${
 isSelected ?"hover:bg-white/20" :"hover:bg-slate-200"
 }`}
 >
 {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
 </button>
 ) : (
 <span className="w-4 h-4 shrink-0 flex items-center justify-center">
 <span
 className="w-1.5 h-1.5 rounded-full"
 style={{ backgroundColor: classColor }}
 />
 </span>
 )}
 <span
 className={`font-mono font-bold text-sm shrink-0 ${
 isSelected ?"text-white" :"text-[#15294F]"
 }`}
 >
 {node.num}
 </span>
 <span
 className={`text-xs truncate ${
 isSelected ?"text-white/90" :"text-slate-600"
 }`}
 >
 {node.lib}
 </span>
 </div>
 {hasChildren && isExpanded && (
 <div>
 {node.children.map((c) => (
 <TreeNode
 key={c.num}
 node={c}
 depth={depth + 1}
 onSelect={onSelect}
 selectedNum={selectedNum}
 expandedSet={expandedSet}
 toggleNode={toggleNode}
 classColor={classColor}
 />
 ))}
 </div>
 )}
 </div>
 );
};

export default function Sidebar({ selectedNum, onSelect, view, onChangeView, favCount }) {
 const tree = useMemo(() => buildTree(), []);
 const [expandedSet, setExpandedSet] = useState(new Set());
 const [openClass, setOpenClass] = useState(null);
 const [query, setQuery] = useState("");

 const filtered = useMemo(() => {
 const q = query.trim();
 if (!q) return null;
 return searchAccounts(q);
 }, [query]);

 const classColorOf = (num) => {
 const cls = CLASSES.find((c) => String(c.id) === String(num)[0]);
 return cls ? cls.color.primary :"#94a3b8";
 };

 const toggleNode = (num) => {
 setExpandedSet((cur) => {
 const next = new Set(cur);
 if (next.has(num)) next.delete(num);
 else next.add(num);
 return next;
 });
 };

 const toggleClass = (id) => {
 setOpenClass((cur) => (cur === id ? null : id));
 };

 // Auto-expansion de l'arborescence à la sélection d'un compte
 // (depuis la PCGMap, la recherche, les favoris, etc.).
 // Pour un compte"164" → ouvre classe 1, déplie 16 et 164 (pour montrer ses enfants).
 useEffect(() => {
 if (!selectedNum) return;
 const numStr = String(selectedNum);
 const classId = parseInt(numStr[0], 10);
 if (!Number.isFinite(classId) || classId < 1 || classId > 8) return;

 // Ouvre la classe parente
 setOpenClass(classId);

 // Déplie tous les préfixes du numéro (≥ 2 chiffres) pour révéler la branche.
 // Ex :"164" → on déplie"16" puis"164" (afin de montrer ses enfants 1641, 1642…).
 setExpandedSet((cur) => {
 const next = new Set(cur);
 for (let i = 2; i <= numStr.length; i++) {
 next.add(numStr.slice(0, i));
 }
 return next;
 });

 // Scroll douce vers le nœud sélectionné si présent dans le DOM
 setTimeout(() => {
 const el = document.querySelector(`[data-testid="tree-node-${numStr}"]`);
 if (el) el.scrollIntoView({ behavior:"smooth", block:"nearest" });
 }, 120);
 }, [selectedNum]);

 return (
 <aside
 data-testid="sidebar"
 className="w-full md:w-80 md:shrink-0 md:border-r md:border-keyto bg-white md:h-screen md:sticky md:top-0 flex flex-col"
 >
 <div className="p-5 border-b border-keyto">
 <div data-testid="sidebar-logo">
 <Logo size="md" onClick={() => onChangeView("home")} />
 </div>
 </div>

 <nav className="px-3 py-3 border-b border-keyto flex flex-col gap-0.5">
 <button
 data-testid="nav-home"
 onClick={() => onChangeView("home")}
 className={`text-left px-3 py-2 rounded-md text-sm font-semibold transition-all ${
 view ==="home"
 ?"bg-[#15294F] text-white"
 :"text-slate-700 hover:bg-slate-100"
 }`}
 >
 Accueil
 </button>

 {[
 {
 group:"Référentiel comptable",
 emoji:"📚",
 items: [
 { id:"carte", label:"Carte interactive du PCG" },
 { id:"reforme", label:"Points clés de la réforme" },
 ],
 },
 {
 group:"Méthodologie comptable",
 emoji:"🔍",
 items: [
 { id:"methodologie", label:"Logique de saisie" },
 { id:"logique", label:"Logique des classes" },
 { id:"ecritures", label:"Écritures fréquentes" },
 { id:"comparateur", label:"Comparateur de comptes" },
 { id:"flux", label:"Liens comptables entre états financiers" },
 ],
 },
 {
 group:"Mise en pratique",
 emoji:"🎯",
 items: [
 { id:"quiz", label:"Auto-évaluation" },
 ],
 },
 {
 group:"Ressources",
 emoji:"📖",
 items: [
 { id:"faq", label:"Questions fréquentes" },
 { id:"glossaire", label:"Glossaire" },
 ],
 },
 ].map((section) => (
 <div key={section.group} className="mt-3 first:mt-2">
 <p
 data-testid={`nav-group-${section.group.toLowerCase().replace(/\s+/g,"-")}`}
 className="px-3 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5"
 >
 <span aria-hidden>{section.emoji}</span>
 <span>{section.group}</span>
 </p>
 {section.items.map((it) => (
 <button
 key={it.id}
 data-testid={`nav-${it.id}`}
 onClick={() => onChangeView(it.id)}
 className={`w-full text-left px-3 py-2 rounded-md text-sm font-semibold transition-all ${
 view === it.id
 ?"bg-[#15294F] text-white"
 :"text-slate-700 hover:bg-slate-100"
 }`}
 >
 {it.label}
 </button>
 ))}
 </div>
 ))}

 <div className="my-3 border-t border-keyto" />

 <button
 data-testid="nav-favorites"
 onClick={() => onChangeView("favorites")}
 className={`text-left px-3 py-2 rounded-md text-sm font-semibold transition-all flex items-center gap-1.5 ${
 view ==="favorites"
 ?"bg-[#15294F] text-white"
 :"text-slate-700 hover:bg-slate-100"
 }`}
 >
 <span aria-hidden>⭐</span>
 <span>Favoris{favCount ? ` (${favCount})` :""}</span>
 </button>
 </nav>

 <div className="flex-1 overflow-y-auto px-2 py-3">
 <div className="px-2 pb-3">
 <div className="relative">
 <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
 <input
 data-testid="sidebar-search"
 type="search"
 value={query}
 onChange={(e) => setQuery(e.target.value)}
 placeholder="Rechercher (ex: 401, TVA…)"
 className="w-full pl-8 pr-8 py-2 text-xs border border-keyto rounded-md bg-slate-50 focus:bg-white focus:outline-none focus:border-[#2D6BE0] focus:ring-2 focus:ring-[#2D6BE0]/15 transition"
 />
 {query && (
 <button
 data-testid="sidebar-search-clear"
 onClick={() => setQuery("")}
 className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-200 text-slate-500"
 aria-label="Effacer la recherche"
 >
 <X size={12} />
 </button>
 )}
 </div>
 </div>

 {filtered ? (
 <div data-testid="sidebar-search-results">
 <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
 {filtered.length === 0 ?"Aucun résultat" : `${filtered.length} résultat${filtered.length > 1 ?"s" :""}`}
 </p>
 {filtered.map((a) => {
 const isSelected = selectedNum === a.num;
 return (
 <button
 key={a.num}
 data-testid={`search-result-${a.num}`}
 onClick={() => onSelect(a.num)}
 className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-left transition-all ${
 isSelected ?"bg-[#15294F] text-white" :"hover:bg-slate-100 text-slate-700"
 }`}
 style={!isSelected ? { borderLeft: `3px solid ${classColorOf(a.num)}` } : undefined}
 >
 <span className={`font-mono font-bold text-xs shrink-0 ${isSelected ?"text-white" :"text-[#15294F]"}`}>
 {a.num}
 </span>
 <span className={`text-xs truncate ${isSelected ?"text-white/90" :"text-slate-600"}`}>
 {a.lib}
 </span>
 </button>
 );
 })}
 </div>
 ) : (
 <>
 <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
 Arborescence du PCG
 </p>
 {CLASSES.map((cls) => {
 const node = tree[cls.id];
 if (!node) return null;
 const isOpen = openClass === cls.id;
 return (
 <div key={cls.id} className="mb-0.5">
 <button
 data-testid={`class-toggle-${cls.id}`}
 onClick={() => toggleClass(cls.id)}
 className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-slate-50 transition-all"
 style={{ borderLeft: `3px solid ${cls.color.primary}` }}
 >
 {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
 <span
 className="font-mono font-extrabold text-lg"
 style={{ color: cls.color.text }}
 >
 {cls.id}
 </span>
 <span className="text-xs font-semibold text-slate-700 text-left flex-1">
 {cls.label}
 </span>
 </button>
 {isOpen && (
 <div className="mt-0.5">
 {node.children.map((c) => (
 <TreeNode
 key={c.num}
 node={c}
 depth={0}
 onSelect={onSelect}
 selectedNum={selectedNum}
 expandedSet={expandedSet}
 toggleNode={toggleNode}
 classColor={cls.color.primary}
 />
 ))}
 </div>
 )}
 </div>
 );
 })}
 </>
 )}
 </div>
 </aside>
 );
}
